import React, { useState } from 'react';
import { TextField, Button, Box, Typography } from '@mui/material';
import HeaderLanding from '../HeaderLanding';
import styles from './Contact.module.css';
import "../style.css";
import contactService from '../../services/contact.service';
import LandingFooter from '../LandingFooter';
import { useBotTrap } from '../../hooks/useBotTrap';
import { BOT_TRAP_FIELDS } from '@common/types/botTrapTypes.ts';

type FormData = {
    name: string;
    email: string;
    message: string;
    phone: string;
};

const Contact = (): JSX.Element => {
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        message: '',
        phone: ''
    });
    const [errors, setErrors] = useState({
        name: false,
        email: false,
    });
    const [sentForm, setSentForm] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [msgError, setMsgError] = useState<boolean>(false)
    const { botTrapField, getBotTrapFields } = useBotTrap(BOT_TRAP_FIELDS.contact);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        setErrors({
            ...errors,
            [e.target.name]: false
        });
    };

    const validateForm = (): boolean => {
        const newErrors = {
            name: formData.name === '',
            email: formData.email === '',
        };

        setErrors(newErrors);

        // if some field is empty, return false
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);
        try {
            await contactService.postFormContact(formData, getBotTrapFields())
            setSentForm(true)
        } catch (error) {
            setMsgError(true)
        } finally {
            setIsSubmitting(false)
        }
    };

    if (sentForm) {
        return (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    justifyContent: 'space-between'
                }}
            >
                <HeaderLanding />
                <Box className={styles.sentFormContainer}>
                    <h2 className={styles.contactTitle}>
                        Thank you for reaching out!<br/>
                    </h2>
                    <h2 className={styles.contactTitle}>
                        We will get back to you as soon as possible.<br/>
                    </h2>
                </Box>
                <LandingFooter />
            </Box>
        )
    }

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                justifyContent: 'space-between'
            }}
        >
            <HeaderLanding />
            <Box className={styles.titleContainer}>
                <h2 className={styles.contactTitleBottom}>
                    Feel free to reach out!<br/>
                </h2>
            </Box>

            <Box
                className={styles.FormContainer}
                component="form"
                onSubmit={(e) => {
                    void handleSubmit(e);
                }}
                sx={{ maxWidth: 600, mx: 'auto', mb: 'auto' }}
            >
                {botTrapField}
                <TextField
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    error={errors.name}
                    helperText={errors.name ? "Name is required" : ""}
                />
                <TextField
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    type='email'
                    error={errors.email}
                    helperText={errors.email ? "Email is required" : ""}
                />
                <TextField
                    label="Phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="How can we help?"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                />
                <Button
                    sx={{ mt: 1 }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={isSubmitting}
                >
                    Submit
                </Button>
                <Typography mt={2} className={styles.msgError}>
                    {
                        msgError && "Something went wrong. Please try again later."
                    }
                </Typography>
            </Box>
            <LandingFooter />
        </Box>
    );
};

export default Contact;
